import React from 'react';
import { Helmet } from 'react-helmet-async';

const LAST_UPDATED = '15 November 2025';

const TermsOfUsePage = () => {
  const clauses = [
    {
      title: '1. Introduction',
      text: `These Terms of Use govern access to receevit.com and the Receevit® client portal (collectively, the “Service”). By creating an account or continuing to use the Service you accept these terms on behalf of the business you represent. Receevit Ltd may update the terms to reflect product, legal or regulatory changes. We will provide at least 30 days’ notice of any material updates via email or in-app notification.`
    },
    {
      title: '2. Eligibility and account responsibilities',
      text: `The Service is available to legitimate businesses and authorised representatives aged 18 or over. You must provide accurate company details, including legal name, trading name, registered address and billing contacts. You are responsible for safeguarding login credentials, ensuring additional users observe these terms and promptly updating account information when it changes.`
    },
    {
      title: '3. Plans, billing and cancellations',
      text: `Receevit® offers subscription tiers (including Essential and Pro) billed in pounds sterling via Stripe. Plan inclusions are described on the pricing page and within the portal. All subscriptions renew automatically until cancelled. You can cancel at any time within the portal; access continues until the end of the paid period. Downgrading may disable features such as chat or advanced automation but your data remains accessible for export.`
    },
    {
      title: '4. Use of the Service',
      text: `We provide a hosted environment for issuing invoices, sharing attachments, automating approvals, communicating with clients and initiating payments through Stripe. You must only upload content you are entitled to share, ensure recipients’ details are accurate and comply with all applicable laws (including tax, invoicing and data protection requirements). You must not misuse the Service, attempt unauthorised access, interfere with other users or upload malicious code.`
    },
    {
      title: '5. Payments and Stripe connectivity',
      text: `Receevit® facilitates payment initiation but does not hold client funds. Stripe processes all card transactions and Stripe Connect routes payouts to your nominated account. You authorise us and Stripe to transmit the instructions required to collect and settle payments. You remain responsible for delivering goods or services referenced on invoices and for any disputes raised by your customers.`
    },
    {
      title: '6. Content, attachments and data ownership',
      text: `You retain ownership of all invoices, documents and data you upload. You grant Receevit Ltd a limited licence to host, process and display that content solely to operate the Service. We may remove content that breaches these terms or applicable law. Please review attachments for sensitivity before uploading them to the portal.`
    },
    {
      title: '7. Service availability and maintenance mode',
      text: `We aim to provide consistent availability but do not offer a formal uptime guarantee. Planned maintenance will usually take place outside core business hours and will be communicated in advance where practicable. During critical updates we may place the Service into Maintenance Mode, limiting actions such as invoice approvals or payments until safeguards are complete.`
    },
    {
      title: '8. Support',
      text: `Support is available during standard business hours via the in-portal help options or support@receevit.com. We may provide additional assistance for Pro plans as described on the pricing page.`
    },
    {
      title: '9. Suspension or termination',
      text: `We may suspend or terminate access if you breach these terms, fail to pay subscription fees, create a security risk, or if we are required to do so by law or a third-party provider. Before termination we will, where reasonable, provide notice and an opportunity to export your data.`
    },
    {
      title: '10. Liability',
      text: `Receevit® provides the Service “as is” and, to the fullest extent permitted by law, excludes implied warranties. We are not liable for indirect or consequential losses, loss of profit, loss of reputation or loss of data caused by third-party systems or incorrect information supplied by you. Our aggregate liability in any 12 month period is limited to the subscription fees paid by you in that period. Nothing in these terms limits liability for death, personal injury, fraud or other liability that cannot be excluded under English law.`
    },
    {
      title: '11. Indemnity',
      text: `You indemnify Receevit® against claims, costs or losses arising from content you upload, unlawful use of the Service or breach of these terms by you or your authorised users.`
    },
    {
      title: '12. Governing law',
      text: `These terms, and any dispute arising from them, are governed by the laws of England and Wales. The courts of England and Wales shall have exclusive jurisdiction.`
    }
  ];

  return (
    <div className="min-h-screen bg-[#1F11ED] px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Terms of Use | Receevit®</title>
        <meta
          name="description"
          content="Read the Receevit® Terms of Use governing access to receevit.com and the Receevit® client portal."
        />
        <link rel="canonical" href="https://www.receevit.com/terms" />
        <meta property="og:title" content="Terms of Use | Receevit®" />
        <meta property="og:description" content="Read the Receevit® Terms of Use governing access to receevit.com and the Receevit® client portal." />
        <meta property="og:url" content="https://www.receevit.com/terms" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.receevit.com/og-image.png" />
      </Helmet>

      <div className="max-w-4xl mx-auto text-white py-24">
        <div className="mb-10">
          <p className="text-sm font-semibold text-white/80 uppercase tracking-wide">Terms of Use</p>
          <h1 className="text-4xl font-bold text-white mt-2 mb-4">The rules for using Receevit®</h1>
          <p className="text-lg text-white/85">
            These terms keep the Receevit® client portal reliable for every subscriber. Please review them carefully before launching invoices or inviting colleagues.
          </p>
          <div className="mt-6 p-4 bg-white/10 rounded-2xl text-sm text-white">
            <strong>Last updated:</strong> {LAST_UPDATED}
          </div>
        </div>

        <div className="space-y-8">
          {clauses.map((clause) => (
            <section key={clause.title}>
              <h2 className="text-2xl font-semibold text-white mb-3">{clause.title}</h2>
              <p className="text-white/85 leading-relaxed">{clause.text}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 p-6 bg-white/10 text-white rounded-3xl">
          <h3 className="text-xl font-semibold mb-2">Questions about these terms?</h3>
          <p>
            Contact <a href="mailto:receevit@outlook.com" className="underline font-semibold">receevit@outlook.com</a> and we will respond as quickly as possible.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUsePage;

